/* eslint-disable @typescript-eslint/no-explicit-any */
import { RefObject } from 'react';
import { FilterSchema } from '@/components/ResourcesSidebar/ResourcesSidebarHelpers';
import { mapFiltersToParams } from '@/pages/Resources/ResourcePageHelpers';
import { useQuery } from '@tanstack/react-query';
import useInfiniteScroll from './useInfiniteScroll';
import { FolderStructureResponse } from '@/lib/types';

export const useFileExplorerData = ({
  filters,
  sortBy,
  searchText,
  search,
  path,
  gameIds,
  gameGroup,
  scrollContainerRef,
  folderStructureFn,
  folderStructureQueryKey,
  fetchFilesByFolderFn,
  fetchFilesByFolderQueryKey,
  fetchFilesFlatFn,
  fetchFilesFlatQueryKey,
  enabled = true,
}: {
  filters: Partial<FilterSchema>;
  sortBy?: string;
  searchText?: string;
  search?: string;
  path: string[];
  gameIds?: number[];
  gameGroup?: string | null;
  scrollContainerRef?:
    | RefObject<HTMLElement>
    | RefObject<HTMLElement | null>;
  enabled?: boolean;
  folderStructureFn: (
    params: any,
  ) => Promise<FolderStructureResponse>;
  folderStructureQueryKey: readonly unknown[];
  fetchFilesByFolderFn: (params: any) => Promise<any>;
  fetchFilesByFolderQueryKey: readonly unknown[];
  fetchFilesFlatFn: (params: any) => Promise<any>;
  fetchFilesFlatQueryKey: readonly unknown[];
}) => {
  const additionalParams: Record<string, string> = {};
  if (searchText && searchText.trim()) {
    additionalParams.file_name = searchText;
  }
  if (search && search.trim()) {
    additionalParams.search = search;
  }

  const filterParams = mapFiltersToParams(
    filters,
    sortBy && sortBy.trim() ? sortBy : undefined,
    additionalParams,
  );

  const folderPath = path.length > 0 ? path.join('/') : '';

  // A game's RTP versions are separate rows sharing one game_group, and marketing assets belong
  // to the logical game — so scope by the group when there is one, or the asset would only show
  // up on the RTP version it happened to be uploaded from. game_group is nullable, hence the
  // gameIds fallback.
  const gameScopeParams = gameGroup
    ? { game_group: [gameGroup] }
    : gameIds
      ? { game_ids: gameIds }
      : {};

  const gameScopeQueryKey = gameGroup
    ? ['gameGroup', gameGroup]
    : gameIds
      ? ['games', ...gameIds]
      : [];

  // The list and grid views both render the same flat, paginated file
  // list (see FileListTable/FileGrid) - folder browsing has no UI
  // consumer, so these two queries stay disabled.
  const {
    data: folderStructure,
    refetch: refetchFolderStructure,
  } = useQuery({
    queryKey: [
      ...folderStructureQueryKey,
      ...gameScopeQueryKey,
      ...Object.values(filterParams),
    ],
    queryFn: () =>
      folderStructureFn({
        ...gameScopeParams,
        ...filterParams,
      }),
    enabled: false,
  });

  const { data: filesData, refetch: refetchFilesByFolder } =
    useInfiniteScroll({
      fetchFn: fetchFilesByFolderFn,
      queryKey: [
        ...fetchFilesByFolderQueryKey,
        folderPath,
        ...gameScopeQueryKey,
        ...Object.values(filterParams),
      ],
      params: {
        folder_path: folderPath || '',
        ...gameScopeParams,
        ...filterParams,
      },
      scrollContainerRef:
        scrollContainerRef as RefObject<HTMLElement> | undefined,
      useWindowScroll: !scrollContainerRef,
      pageSize: 20,
      scrollThreshold: 200,
      enabled: false,
    });

  const {
    data: filesFlatData,
    isLoading: isFilesFlatLoading,
    isFetchingNextPage: isFilesFlatFetchingNextPage,
    total_count: filesFlatTotalCount,
    refetch: refetchFilesFlat,
  } = useInfiniteScroll({
    fetchFn: fetchFilesFlatFn,
    queryKey: [
      ...fetchFilesFlatQueryKey,
      ...gameScopeQueryKey,
      ...Object.values(filterParams),
    ],
    params: {
      ...gameScopeParams,
      ...filterParams,
    },
    scrollContainerRef:
      scrollContainerRef as RefObject<HTMLElement> | undefined,
    useWindowScroll: !scrollContainerRef,
    pageSize: 20,
    scrollThreshold: 200,
    enabled,
  });

  const refetch = () => {
    refetchFolderStructure();
    refetchFilesByFolder();
    refetchFilesFlat();
  };

  return {
    folderStructure,
    filesData,
    filesFlatData,
    // Total matching the current filters, not the number loaded - the
    // basket bar shows them as "N of M files loaded".
    totalCount: filesFlatTotalCount,
    loading: isFilesFlatLoading,
    loadingNextPage: isFilesFlatFetchingNextPage,
    refetch,
  };
};
